"use strict";
// Copyright (c) 2025 MDFriday. All rights reserved.
// This software is proprietary and confidential.
// Use of this source code is governed by the license that can be found in the LICENSE file.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ItemType = void 0;
exports.lex = lex;
// ItemType identifies the type of lex items.
var ItemType;
(function (ItemType) {
    ItemType[ItemType["itemError"] = 0] = "itemError";
    ItemType[ItemType["itemBool"] = 1] = "itemBool";
    ItemType[ItemType["itemChar"] = 2] = "itemChar";
    ItemType[ItemType["itemCharConstant"] = 3] = "itemCharConstant";
    ItemType[ItemType["itemComment"] = 4] = "itemComment";
    ItemType[ItemType["itemComplex"] = 5] = "itemComplex";
    ItemType[ItemType["itemAssign"] = 6] = "itemAssign";
    ItemType[ItemType["itemDeclare"] = 7] = "itemDeclare";
    ItemType[ItemType["itemEOF"] = 8] = "itemEOF";
    ItemType[ItemType["itemField"] = 9] = "itemField";
    ItemType[ItemType["itemIdentifier"] = 10] = "itemIdentifier";
    ItemType[ItemType["itemLeftDelim"] = 11] = "itemLeftDelim";
    ItemType[ItemType["itemLeftParen"] = 12] = "itemLeftParen";
    ItemType[ItemType["itemNumber"] = 13] = "itemNumber";
    ItemType[ItemType["itemPipe"] = 14] = "itemPipe";
    ItemType[ItemType["itemRawString"] = 15] = "itemRawString";
    ItemType[ItemType["itemRightDelim"] = 16] = "itemRightDelim";
    ItemType[ItemType["itemRightParen"] = 17] = "itemRightParen";
    ItemType[ItemType["itemSpace"] = 18] = "itemSpace";
    ItemType[ItemType["itemString"] = 19] = "itemString";
    ItemType[ItemType["itemText"] = 20] = "itemText";
    ItemType[ItemType["itemVariable"] = 21] = "itemVariable";
    // Keywords appear after all the rest.
    ItemType[ItemType["itemKeyword"] = 22] = "itemKeyword";
    ItemType[ItemType["itemBlock"] = 23] = "itemBlock";
    ItemType[ItemType["itemBreak"] = 24] = "itemBreak";
    ItemType[ItemType["itemContinue"] = 25] = "itemContinue";
    ItemType[ItemType["itemDot"] = 26] = "itemDot";
    ItemType[ItemType["itemDefine"] = 27] = "itemDefine";
    ItemType[ItemType["itemElse"] = 28] = "itemElse";
    ItemType[ItemType["itemEnd"] = 29] = "itemEnd";
    ItemType[ItemType["itemIf"] = 30] = "itemIf";
    ItemType[ItemType["itemNil"] = 31] = "itemNil";
    ItemType[ItemType["itemRange"] = 32] = "itemRange";
    ItemType[ItemType["itemTemplate"] = 33] = "itemTemplate";
    ItemType[ItemType["itemWith"] = 34] = "itemWith";
})(ItemType || (exports.ItemType = ItemType = {}));
const key = {
    ".": ItemType.itemDot,
    "block": ItemType.itemBlock,
    "break": ItemType.itemBreak,
    "continue": ItemType.itemContinue,
    "define": ItemType.itemDefine,
    "else": ItemType.itemElse,
    "end": ItemType.itemEnd,
    "if": ItemType.itemIf,
    "range": ItemType.itemRange,
    "nil": ItemType.itemNil,
    "template": ItemType.itemTemplate,
    "with": ItemType.itemWith,
};
const EOF = -1;
// Constants for trimming spaces
const spaceChars = " \t\r\n"; // These are the space characters defined by Go itself.
const trimMarker = '-'; // Attached to left/right delimiter, trims trailing spaces from preceding/following text.
const trimMarkerLen = 2; // marker plus space before or after
const leftDelim = "{{";
const rightDelim = "}}";
const leftComment = "/*";
const rightComment = "*/";
class Lexer {
    constructor(name, input, left, right) {
        this.name = name;
        this.input = input;
        this.leftDelim = left || leftDelim;
        this.rightDelim = right || rightDelim;
        this.pos = 0;
        this.start = 0;
        this.atEOF = false;
        this.parenDepth = 0;
        this.line = 1;
        this.startLine = 1;
        this.insideAction = false;
        this.options = {
            emitComment: false,
            breakOK: false,
            continueOK: false
        };
        this.item = {
            typ: ItemType.itemEOF,
            pos: 0,
            val: "",
            line: 1
        };
    }
    // next returns the next rune in the input
    next() {
        if (this.pos >= this.input.length) {
            this.atEOF = true;
            return String.fromCharCode(EOF);
        }
        const r = this.input[this.pos];
        this.pos++;
        if (r === '\n') {
            this.line++;
        }
        return r;
    }
    // peek returns but does not consume the next rune in the input
    peek() {
        const r = this.next();
        this.backup();
        return r;
    }
    // backup steps back one rune
    backup() {
        if (!this.atEOF && this.pos > 0) {
            this.pos--;
            // Correct newline count
            if (this.input[this.pos] === '\n') {
                this.line--;
            }
        }
    }
    // emit passes an item back to the client
    emit(t) {
        this.item = {
            typ: t,
            pos: this.start,
            val: this.input.slice(this.start, this.pos),
            line: this.startLine
        };
        this.start = this.pos;
        this.startLine = this.line;
        return null;
    }
    // ignore skips over the pending input before this point
    ignore() {
        this.line += (this.input.slice(this.start, this.pos).match(/\n/g) || []).length;
        this.start = this.pos;
        this.startLine = this.line;
    }
    // accept consumes the next rune if it's from the valid set
    accept(valid) {
        if (valid.includes(this.next())) {
            return true;
        }
        this.backup();
        return false;
    }
    // acceptRun consumes a run of runes from the valid set
    acceptRun(valid) {
        while (valid.includes(this.next())) {
            // continue
        }
        this.backup();
    }
    // errorf returns an error token and terminates the scan
    errorf(format, ...args) {
        this.item = {
            typ: ItemType.itemError,
            pos: this.start,
            val: format.replace(/%[sdq%]/g, () => {
                const arg = args.shift();
                return String(arg);
            }),
            line: this.startLine
        };
        return null;
    }
    // nextItem returns the next item from the input
    nextItem() {
        this.item = {
            typ: ItemType.itemEOF,
            pos: this.pos,
            val: "EOF",
            line: this.startLine
        };
        let state = this.insideAction ? lexInsideAction : lexText;
        while (state) {
            state = state(this);
            if (this.item.typ === ItemType.itemError) {
                return this.item; // 立即返回错误 item
            }
        }
        return this.item;
    }
    // atRightDelim reports whether the lexer is at a right delimiter, possibly preceded by a trim marker.
    atRightDelim() {
        const s = this.input.slice(this.pos);
        if (s.length >= 2 && isSpace(s[0]) && s[1] === trimMarker &&
            s.slice(trimMarkerLen).startsWith(this.rightDelim)) {
            return [true, true];
        }
        if (s.startsWith(this.rightDelim)) {
            return [true, false];
        }
        return [false, false];
    }
    // atTerminator reports whether the input is at valid termination character
    atTerminator() {
        const r = this.peek();
        if (isSpace(r)) {
            return true;
        }
        switch (r) {
            case String.fromCharCode(EOF):
            case '.':
            case ',':
            case '|':
            case ':':
            case ')':
            case '(':
                return true;
        }
        return this.input.slice(this.pos).startsWith(this.rightDelim);
    }
    scanNumber() {
        // Optional leading sign
        this.accept("+-");
        // Is it hex?
        let digits = "0123456789_";
        if (this.accept("0")) {
            if (this.accept("xX")) {
                digits = "0123456789abcdefABCDEF_";
            }
            else if (this.accept("oO")) {
                digits = "01234567_";
            }
            else if (this.accept("bB")) {
                digits = "01_";
            }
        }
        this.acceptRun(digits);
        if (this.accept(".")) {
            this.acceptRun(digits);
        }
        if (digits === "0123456789_" && this.accept("eE")) {
            this.accept("+-");
            this.acceptRun("0123456789_");
        }
        if (digits === "0123456789abcdefABCDEF_" && this.accept("pP")) {
            this.accept("+-");
            this.acceptRun("0123456789_");
        }
        // Is it imaginary?
        this.accept("i");
        // Next thing mustn't be alphanumeric
        if (isAlphaNumeric(this.peek())) {
            this.next();
            return false;
        }
        return true;
    }
    // thisItem returns the item at the current input point with the specified type
    // and advances the input.
    thisItem(t) {
        const i = {
            typ: t,
            pos: this.start,
            val: this.input.slice(this.start, this.pos),
            line: this.startLine
        };
        this.start = this.pos;
        this.startLine = this.line;
        return i;
    }
    // emitItem passes the specified item to the parser.
    emitItem(i) {
        this.item = i;
        return null;
    }
}
// Helper functions
function isSpace(r) {
    return r === ' ' || r === '\t' || r === '\r' || r === '\n';
}
function isAlphaNumeric(r) {
    return r === '_' || /[a-zA-Z0-9]/.test(r);
}
function leftTrimLength(s) {
    let i = 0;
    while (i < s.length && spaceChars.includes(s[i])) {
        i++;
    }
    return i;
}
function rightTrimLength(s) {
    let i = s.length - 1;
    while (i >= 0 && spaceChars.includes(s[i])) {
        i--;
    }
    return s.length - (i + 1);
}
function hasLeftTrimMarker(s) {
    return s.length >= 2 && s[0] === trimMarker && spaceChars.includes(s[1]);
}
function hasRightTrimMarker(s) {
    return s.length >= 2 && spaceChars.includes(s[0]) && s[1] === trimMarker;
}
// State functions
function lexText(l) {
    const x = l.input.indexOf(l.leftDelim, l.pos);
    if (x >= 0) {
        if (x > l.pos) {
            l.pos += x - l.pos;
            // Do we trim any trailing space?
            let trimLength = 0;
            const delimEnd = l.pos + l.leftDelim.length;
            if (hasLeftTrimMarker(l.input.slice(delimEnd))) {
                trimLength = rightTrimLength(l.input.slice(l.start, l.pos));
            }
            l.pos -= trimLength;
            l.line += (l.input.slice(l.start, l.pos).match(/\n/g) || []).length;
            const i = l.thisItem(ItemType.itemText);
            l.pos += trimLength;
            l.ignore();
            if (i.val.length > 0) {
                return l.emitItem(i);
            }
        }
        return lexLeftDelim;
    }
    l.pos = l.input.length;
    // Correctly reached EOF.
    if (l.pos > l.start) {
        l.line += (l.input.slice(l.start, l.pos).match(/\n/g) || []).length;
        return l.emit(ItemType.itemText);
    }
    return l.emit(ItemType.itemEOF);
}
function lexLeftDelim(l) {
    l.pos += l.leftDelim.length;
    const trimSpace = hasLeftTrimMarker(l.input.slice(l.pos));
    let afterMarker = 0;
    if (trimSpace) {
        afterMarker = trimMarkerLen;
    }
    if (l.input.slice(l.pos + afterMarker).startsWith(leftComment)) {
        l.pos += afterMarker;
        l.ignore();
        return lexComment;
    }
    const i = l.thisItem(ItemType.itemLeftDelim);
    l.insideAction = true;
    l.pos += afterMarker;
    l.ignore();
    l.parenDepth = 0;
    return l.emitItem(i);
}
function lexComment(l) {
    l.pos += leftComment.length;
    // 使用相对位置计算，模拟 Go 的 strings.Index 行为
    const remainingInput = l.input.slice(l.pos);
    const x = remainingInput.indexOf(rightComment);
    if (x < 0) {
        return l.errorf("unclosed comment");
    }
    l.pos += x + rightComment.length;
    const [delim, trimSpace] = l.atRightDelim();
    if (!delim) {
        return l.errorf("comment ends before closing delimiter");
    }
    const i = l.thisItem(ItemType.itemComment);
    if (trimSpace) {
        l.pos += trimMarkerLen;
    }
    l.pos += l.rightDelim.length;
    if (trimSpace) {
        l.pos += leftTrimLength(l.input.slice(l.pos));
    }
    l.ignore();
    if (l.options.emitComment) {
        return l.emitItem(i);
    }
    return lexText;
}
function lexRightDelim(l) {
    const [_, trimSpace] = l.atRightDelim();
    if (trimSpace) {
        l.pos += trimMarkerLen;
        l.ignore();
    }
    l.pos += l.rightDelim.length;
    const i = l.thisItem(ItemType.itemRightDelim);
    if (trimSpace) {
        l.pos += leftTrimLength(l.input.slice(l.pos));
        l.ignore();
    }
    l.insideAction = false;
    return l.emitItem(i);
}
function lexInsideAction(l) {
    const [delim, _] = l.atRightDelim();
    if (delim) {
        if (l.parenDepth === 0) {
            return lexRightDelim;
        }
        return l.errorf("unclosed left paren");
    }
    const r = l.next();
    if (r === String.fromCharCode(EOF)) {
        return l.errorf("unclosed action");
    }
    if (isSpace(r)) {
        l.backup();
        return lexSpace;
    }
    switch (r) {
        case '=':
            return l.emit(ItemType.itemAssign);
        case ':':
            if (l.next() !== '=') {
                return l.errorf("expected :=");
            }
            return l.emit(ItemType.itemDeclare);
        case '|':
            return l.emit(ItemType.itemPipe);
        case '"':
            return lexQuote;
        case '`':
            return lexRawQuote;
        case '$':
            return lexVariable;
        case '\'':
            return lexChar;
        case '.':
            if (l.pos < l.input.length) {
                const nextChar = l.input[l.pos];
                if (!/[0-9]/.test(nextChar)) {
                    return lexField;
                }
            }
            l.backup();
            return lexNumber;
        case '+':
        case '-':
            l.backup();
            return lexNumber;
        case '(':
            l.parenDepth++;
            return l.emit(ItemType.itemLeftParen);
        case ')':
            l.parenDepth--;
            if (l.parenDepth < 0) {
                return l.errorf("unexpected right paren");
            }
            return l.emit(ItemType.itemRightParen);
        default:
            if (/[0-9]/.test(r)) {
                l.backup();
                return lexNumber;
            }
            if (isAlphaNumeric(r)) {
                l.backup();
                return lexIdentifier;
            }
            // 使用更准确的可打印 ASCII 字符判断
            if (r.charCodeAt(0) <= 127 && /[\x20-\x7E]/.test(r)) {
                return l.emit(ItemType.itemChar);
            }
            const codePoint = r.codePointAt(0) || 0;
            return l.errorf(`unrecognized character in action: U+${codePoint.toString(16).padStart(4, '0').toUpperCase()}`);
    }
}
function lexSpace(l) {
    let numSpaces = 0;
    while (true) {
        const r = l.peek();
        if (!isSpace(r)) {
            break;
        }
        l.next();
        numSpaces++;
    }
    if (hasRightTrimMarker(l.input.slice(l.pos - 1)) &&
        l.input.slice(l.pos - 1 + trimMarkerLen).startsWith(l.rightDelim)) {
        l.backup();
        if (numSpaces === 1) {
            return lexRightDelim;
        }
    }
    return l.emit(ItemType.itemSpace);
}
function lexIdentifier(l) {
    while (true) {
        const r = l.next();
        if (!isAlphaNumeric(r)) {
            l.backup();
            const word = l.input.slice(l.start, l.pos);
            if (!l.atTerminator()) {
                return l.errorf(`bad character ${r}`);
            }
            if (key[word] > ItemType.itemKeyword) {
                const item = key[word];
                if ((item === ItemType.itemBreak && !l.options.breakOK) ||
                    (item === ItemType.itemContinue && !l.options.continueOK)) {
                    return l.emit(ItemType.itemIdentifier);
                }
                return l.emit(item);
            }
            if (word[0] === '.') {
                return l.emit(ItemType.itemField);
            }
            if (word === "true" || word === "false") {
                return l.emit(ItemType.itemBool);
            }
            return l.emit(ItemType.itemIdentifier);
        }
    }
}
function lexField(l) {
    return lexFieldOrVariable(l, ItemType.itemField);
}
function lexVariable(l) {
    if (l.atTerminator()) {
        return l.emit(ItemType.itemVariable);
    }
    return lexFieldOrVariable(l, ItemType.itemVariable);
}
function lexFieldOrVariable(l, typ) {
    if (l.atTerminator()) {
        if (typ === ItemType.itemVariable) {
            return l.emit(ItemType.itemVariable);
        }
        return l.emit(ItemType.itemDot);
    }
    while (true) {
        const r = l.next();
        if (!isAlphaNumeric(r)) {
            l.backup();
            break;
        }
    }
    if (!l.atTerminator()) {
        return l.errorf(`bad character ${l.peek()}`);
    }
    return l.emit(typ);
}
function lexChar(l) {
    const startPos = l.pos - 1; // Include the opening quote
    Loop: while (true) {
        const r = l.next();
        switch (r) {
            case '\\': {
                const next = l.next();
                if (next === String.fromCharCode(EOF) || next === '\n') {
                    return l.errorf("unterminated character constant");
                }
                continue Loop; // Continue to next character, preserving the escape sequence
            }
            case String.fromCharCode(EOF):
            case '\n':
                return l.errorf("unterminated character constant");
            case '\'':
                break Loop;
        }
    }
    // Use the original input text
    l.item = {
        typ: ItemType.itemCharConstant,
        pos: l.start,
        val: l.input.slice(startPos, l.pos), // Include quotes
        line: l.startLine
    };
    l.start = l.pos;
    return null;
}
function lexNumber(l) {
    if (!l.scanNumber()) {
        return l.errorf(`bad number syntax: "${l.input.slice(l.start, l.pos)}"`);
    }
    if (l.peek() === '+' || l.peek() === '-') {
        // Complex: 1+2i. No spaces, must end in 'i'.
        if (!l.scanNumber() || l.input[l.pos - 1] !== 'i') {
            return l.errorf(`bad number syntax: "${l.input.slice(l.start, l.pos)}"`);
        }
        return l.emit(ItemType.itemComplex);
    }
    return l.emit(ItemType.itemNumber);
}
function lexQuote(l) {
    const startPos = l.pos - 1; // Include the opening quote
    Loop: while (true) {
        const r = l.next();
        switch (r) {
            case '\\': {
                const next = l.next();
                if (next === String.fromCharCode(EOF) || next === '\n') {
                    return l.errorf("unterminated quoted string");
                }
                continue Loop; // Continue to next character, preserving the escape sequence
            }
            case String.fromCharCode(EOF):
            case '\n':
                return l.errorf("unterminated quoted string");
            case '"':
                break Loop;
        }
    }
    // Use the original input text
    l.item = {
        typ: ItemType.itemString,
        pos: l.start,
        val: l.input.slice(startPos, l.pos), // Include quotes
        line: l.startLine
    };
    l.start = l.pos;
    return null;
}
function lexRawQuote(l) {
    while (true) {
        const r = l.next();
        if (r === String.fromCharCode(EOF)) {
            return l.errorf("unterminated raw quoted string");
        }
        if (r === '`') {
            break;
        }
    }
    return l.emit(ItemType.itemRawString);
}
// Export the main lexer creation function
function lex(name, input, left = "", right = "") {
    return new Lexer(name, input, left, right);
}
//# sourceMappingURL=lex.js.map